// ============================================
// COMPONENT - ModalBoletaConsultada
// ============================================
// Modal público que muestra el detalle de una boleta o voucher encontrado por la
// consulta pública, con opción de reimprimir el ticket térmico.

import { useRef, useState } from 'react';
import { MdClose, MdReceiptLong, MdPrint } from 'react-icons/md';
import type { ConsultaBoletaPublicaResult } from '@almacenes/core';
import { Button } from '@almacenes/ui';
import { useFocusTrap } from '../../tienda/hooks/use-focus-trap';
import { buildTicketHtml, fetchPdf417, formatClp, montoDeLinea, muestraPrecioLista } from '../../../utils/ticket-html';
import { readPrintConfig, printTicketHtml } from '../../../hooks/usePrintConfig';

interface ModalBoletaConsultadaProps {
  isOpen: boolean;
  onClose: () => void;
  boleta: ConsultaBoletaPublicaResult | null;
}

const formatFecha = (fecha: string) => {
  const d = new Date(fecha);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const ModalBoletaConsultada = ({ isOpen, onClose, boleta }: ModalBoletaConsultadaProps) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useFocusTrap(modalRef, onClose);

  if (!isOpen || !boleta) return null;

  const handleImprimir = async () => {
    setIsPrinting(true);
    try {
      const printConfig = readPrintConfig();
      const imagenPDF417 = await fetchPdf417(boleta);
      const html = buildTicketHtml(boleta, imagenPDF417, printConfig);
      await printTicketHtml(html, printConfig);
    } catch (error) {
      console.error('Error al imprimir boleta:', error);
    } finally {
      setIsPrinting(false);
    }
  };

  const numeroDocumento = boleta.electronicReceipt ? boleta.siiFolio : boleta.boletaId;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 shrink-0 bg-blue-100 rounded-lg flex items-center justify-center">
              <MdReceiptLong className="text-xl text-[#43BFFF]" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-[#11194F] truncate">
                {boleta.electronicReceipt ? 'Boleta Electrónica' : 'Voucher'}
              </h2>
              <p className="text-sm text-gray-500">N° {numeroDocumento}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors"
            aria-label="Cerrar"
          >
            <MdClose className="text-2xl" />
          </button>
        </div>

        {/* Datos del almacén / documento */}
        <div className="p-4 sm:p-6 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {boleta.electronicReceipt && (
              <>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Razón Social</p>
                  <p className="text-sm font-semibold text-[#11194F]">{boleta.contribuyenteRazonSocial}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Giro</p>
                  <p className="text-sm font-semibold text-[#11194F]">{boleta.contribuyenteGiro}</p>
                </div>
              </>
            )}
            <div>
              <p className="text-xs text-gray-500 mb-1">Almacén</p>
              <p className="text-sm font-semibold text-[#11194F]">{boleta.nombreAlmacen}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Dirección</p>
              <p className="text-sm font-semibold text-[#11194F]">{boleta.direccionAlmacen}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Fecha</p>
              <p className="text-sm font-semibold text-[#11194F]">{formatFecha(boleta.fecha)}</p>
            </div>
          </div>
          {!boleta.electronicReceipt && (
            <p className="mt-4 text-xs italic text-gray-500">
              *Voucher no válido como boleta electrónica*
            </p>
          )}
        </div>

        {/* Tabla de productos */}
        <div className="p-4 sm:p-6">
          <h3 className="text-lg font-bold text-[#11194F] mb-4">Detalle</h3>
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="w-full min-w-[420px]">
              <thead className="bg-[#F3F4F9]">
                <tr>
                  <th className="px-3 sm:px-4 py-3 text-center text-sm font-bold text-[#11194F] rounded-l-lg">Cantidad</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-sm font-bold text-[#11194F]">Producto</th>
                  <th className="px-3 sm:px-4 py-3 text-right text-sm font-bold text-[#11194F] rounded-r-lg">Precio</th>
                </tr>
              </thead>
              <tbody>
                {boleta.details.map((detalle, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-900 text-center">{detalle.cantidad}</td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-900">
                      {detalle.nombreProducto}
                      {detalle.descuento != null && detalle.descuento > 0 && (
                        <span className="block text-xs italic text-gray-500">
                          Descuento: -{formatClp(detalle.descuento)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm font-semibold text-[#11194F] text-right whitespace-nowrap">
                      {formatClp(montoDeLinea(boleta, detalle))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
            {boleta.electronicReceipt && boleta.iva > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>IVA (19%)</span>
                <span>${boleta.iva.toLocaleString('es-CL')}</span>
              </div>
            )}
            {muestraPrecioLista(boleta) && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span>
                <span>{formatClp(boleta.subtotalSinDescuento ?? boleta.total)}</span>
              </div>
            )}
            {boleta.descuento > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Dscto Total</span>
                <span>-{formatClp(boleta.descuento)}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2">
              <span className="text-lg font-bold text-[#11194F]">Total</span>
              <span className="text-2xl font-bold text-[#43BFFF]">
                ${boleta.total.toLocaleString('es-CL')}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <Button variant="neutral" className="w-full sm:w-auto" onClick={onClose}>
            Cerrar
          </Button>
          <Button variant="primary" className="w-full sm:w-auto" onClick={handleImprimir} isLoading={isPrinting}>
            <span className="inline-flex items-center justify-center gap-2">
              <MdPrint className="text-xl" />
              Imprimir
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
